import {type SingleActionResult} from '@sanity/client'
import {useMemo} from 'react'

import {FEATURES, useFeatureEnabled} from '../../hooks/useFeatureEnabled'
import {useResourceCache} from '../../store/ResourceCacheProvider'
import {createReleasePermissionsStore} from './createReleasePermissionsStore'

const RELEASE_PERMISSIONS_RESOURCE_CACHE_NAMESPACE = 'ReleasePermissions'

export interface useReleasePermissionsValue {
  checkWithPermissionGuard: <T extends (...args: any[]) => Promise<void | SingleActionResult>>(
    action: T,
    ...args: Parameters<T>
  ) => Promise<boolean>
  permissions: {[key: string]: boolean}
}

/**
 * @internal
 */
export function useReleasePermissions(): useReleasePermissionsValue {
  const resourceCache = useResourceCache()
  const contentReleasesFeature = useFeatureEnabled(FEATURES.contentReleases)

  return useMemo(() => {
    const releasePermissionsStore =
      resourceCache.get<useReleasePermissionsValue>({
        dependencies: [contentReleasesFeature],
        namespace: RELEASE_PERMISSIONS_RESOURCE_CACHE_NAMESPACE,
      }) || createReleasePermissionsStore(contentReleasesFeature.enabled)

    resourceCache.set({
      namespace: RELEASE_PERMISSIONS_RESOURCE_CACHE_NAMESPACE,
      value: releasePermissionsStore,
      dependencies: [contentReleasesFeature],
    })

    return releasePermissionsStore
  }, [contentReleasesFeature, resourceCache])
}
